import React, { useState } from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { compensarEscalaVh } from '../../utils/formHelpers';

/**
 * SqlexecMensaje - Modal profesional para mostrar errores con soluciones
 * Zero Hardcode - Todo desde props y sisconfig
 */
const SqlexecMensaje = ({
  show = false,
  type = 'error', // error | warning | info | success
  title,
  message,
  steps = [],
  technicalInfo = null,
  actions = [],
  onClose
}) => {
  const { modales_ui, botones_ui } = useSisConfig();
  const [showTechnical, setShowTechnical] = useState(false);

  if (!show) return null;

  // Configuración según tipo
  const config = {
    error: { 
      Icon: XCircle, 
      iconBg: 'bg-red-100', 
      iconColor: 'text-red-600',
      borderColor: 'border-red-200',
      bgColor: 'bg-red-50'
    },
    warning: { 
      Icon: AlertTriangle, 
      iconBg: 'bg-orange-100', 
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      bgColor: 'bg-orange-50'
    },
    info: { 
      Icon: Info, 
      iconBg: 'bg-blue-100', 
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      bgColor: 'bg-blue-50'
    },
    success: { 
      Icon: CheckCircle, 
      iconBg: 'bg-green-100', 
      iconColor: 'text-green-600',
      borderColor: 'border-green-200',
      bgColor: 'bg-green-50'
    }
  };

  const { Icon, iconBg, iconColor, borderColor, bgColor } = config[type] || config.error;

  // Bloqueo fiscal controlado (MAPASAC-*, backend/routes/sqlexec.js): el
  // mensaje ya trae la causa y la accion, y termina en "Referencia: MAPASAC-...";
  // los pasos genericos del formulario (cliente/productos) no aplican.
  const esBloqueoFiscal = typeof message === 'string' && /Referencia: MAPASAC-[A-Z0-9-]+\s*$/.test(message);
  const pasos = esBloqueoFiscal ? [] : steps;

  const containerStyles = modales_ui?.container || {};
  const headerStyles = modales_ui?.header || {};
  const contentStyles = modales_ui?.content || {};
  const btnBase = botones_ui || {};
  const btnPrimary = btnBase.primary || {};
  const btnSecondary = btnBase.secondary || {};

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[70] p-4"
      style={{
        backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.7)',
        backdropFilter: `blur(${modales_ui?.overlay?.backdropBlur || '4px'})`
      }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full mx-4"
        style={{
          maxWidth: '600px',
          maxHeight: 'calc(100vh - 2rem)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: containerStyles.backgroundColor || '#ffffff',
          borderRadius: containerStyles.borderRadius || '12px',
          boxShadow: containerStyles.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: containerStyles.border || '1px solid rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Header con icono */}
        <div 
          className="flex items-center p-6"
          style={{ gap: headerStyles.iconGap || '0.75rem', flexShrink: 0 }}
        >
          <div 
            className={`${iconBg} rounded-lg flex items-center justify-center`}
            style={{
              padding: headerStyles.iconPadding || '0.75rem',
              borderRadius: headerStyles.iconBorderRadius || '0.75rem'
            }}
          >
            <Icon 
              className={iconColor} 
              size={parseInt(headerStyles.iconSize) || 32} 
            />
          </div>
          <h3 
            className="font-semibold flex-1"
            style={{
              fontSize: headerStyles.fontSize || '1.5rem',
              fontWeight: headerStyles.fontWeight || '600',
              color: headerStyles.textColor || '#111827'
            }}
          >
            {title}
          </h3>
        </div>

        {/* Cuerpo con scroll: ocupa el espacio que dejan titulo y botones
            (que quedan siempre visibles); nunca scroll horizontal */}
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: compensarEscalaVh(contentStyles.maxHeight || '60vh'),
            overflowY: 'auto',
            overflowX: 'hidden',
            overflowWrap: 'anywhere'
          }}
        >
        {/* Mensaje principal */}
        <div className="px-6 pb-4">
          <p 
            style={{
              fontSize: '0.875rem',
              color: '#374151',
              lineHeight: '1.5',
              whiteSpace: 'pre-line'
            }}
          >
            {message}
          </p>
        </div>

        {/* Pasos de solución */}
        {pasos.length > 0 && (
          <div className="px-6 pb-4">
            <div 
              className={`${bgColor} border ${borderColor} rounded-lg p-4`}
            >
              <p 
                className="font-medium mb-3"
                style={{
                  fontSize: '0.875rem',
                  color: '#1f2937'
                }}
              >
                📋 Pasos para solucionar:
              </p>
              <ol 
                className="space-y-2 list-decimal list-inside"
                style={{
                  fontSize: '0.8125rem',
                  color: '#4b5563'
                }}
              >
                {pasos.map((step, i) => (
                  <li key={i} style={{ lineHeight: '1.5' }}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {/* Info técnica colapsable */}
        {technicalInfo && (
          <div className="px-6 pb-4">
            <button
              onClick={() => setShowTechnical(!showTechnical)}
              className="flex items-center gap-1 transition-colors"
              style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.color = '#111827'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              {showTechnical ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              <span>Información técnica</span>
            </button>
            {showTechnical && (
              <pre 
                className="mt-2 p-3 rounded"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.75rem',
                  backgroundColor: '#f3f4f6',
                  color: '#1f2937',
                  fontFamily: 'monospace'
                }}
              >
                {JSON.stringify(technicalInfo, null, 2)}
              </pre>
            )}
          </div>
        )}

        </div>

        {/* Botones de acción */}
        <div 
          className="flex justify-end p-6 border-t"
          style={{
            flexShrink: 0,
            gap: '0.75rem',
            borderTopColor: '#e5e7eb'
          }}
        >
          {actions.length > 0 ? (
            actions.map((action, i) => {
              const isSecondary = action.variant === 'secondary';
              const btnStyle = isSecondary ? btnSecondary : btnPrimary;

              return (
                <button
                  key={i}
                  onClick={action.onClick || onClose}
                  className="transition-colors"
                  style={{
                    height: btnBase.height || '36px',
                    fontSize: btnBase.fontSize || '0.875rem',
                    fontFamily: btnBase.fontFamily || 'inherit',
                    fontWeight: btnBase.fontWeight || '500',
                    paddingLeft: '1.25rem',
                    paddingRight: '1.25rem',
                    paddingTop: btnBase.padding?.y || '0.5rem',
                    paddingBottom: btnBase.padding?.y || '0.5rem',
                    borderRadius: btnBase.borderRadius || '6px',
                    borderWidth: btnBase.borderWidth || '1px',
                    borderColor: btnStyle.borderColor || (isSecondary ? '#d1d5db' : '#3b82f6'),
                    backgroundColor: btnStyle.backgroundColor || (isSecondary ? '#ffffff' : '#3b82f6'),
                    color: btnStyle.textColor || (isSecondary ? '#374151' : '#ffffff'),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = btnStyle.hoverBackgroundColor || (isSecondary ? '#f9fafb' : '#2563eb');
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = btnStyle.backgroundColor || (isSecondary ? '#ffffff' : '#3b82f6');
                  }}
                >
                  {action.label}
                </button>
              );
            })
          ) : (
            <button
              onClick={onClose}
              className="transition-colors"
              style={{
                height: btnBase.height || '36px',
                fontSize: btnBase.fontSize || '0.875rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: '1.25rem',
                paddingRight: '1.25rem',
                paddingTop: btnBase.padding?.y || '0.5rem',
                paddingBottom: btnBase.padding?.y || '0.5rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: btnPrimary.borderColor || '#3b82f6',
                backgroundColor: btnPrimary.backgroundColor || '#3b82f6',
                color: btnPrimary.textColor || '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = btnPrimary.hoverBackgroundColor || '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = btnPrimary.backgroundColor || '#3b82f6';
              }}
            >
              Entendido
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SqlexecMensaje;